"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { strings } from "@perceptron/constants";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { clamp } from "@perceptron/utils/math";
import type { EmblaCarouselType, EmblaEventType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";

import { TemplateDefinition } from "@assets/templates";
import { useIsMobile } from "@demo/hooks/useIsMobile";

import {
  CarouselConfig,
  CAROUSEL,
  COMPACT_CAROUSEL,
  MOBILE_CAROUSEL,
  parseMaxBlurPx,
  SLOT_STYLE,
  STACKED_LAYOUT_BREAKPOINT_PX,
} from "./config";
import Slot from "./Slot";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

/** `panel` = examples sheet (desktop + mobile-stacked); `inline` = compact preview below the prompt. */
type FeaturedCarouselVariant = "panel" | "inline";

type FeaturedCarouselProps = {
  templates: ReadonlyArray<TemplateDefinition>;
  onSelect: (template: TemplateDefinition) => void;
  variant?: FeaturedCarouselVariant;
};

const AUTOPLAY_DELAY_MS = 3000;
const TWEEN_FACTOR_BASE = 0.52;
/** Scroll-progress movement across a pointer gesture above which it counts as a swipe rather than a tap. */
const DRAG_PROGRESS_EPSILON = 0.001;

/** Feathers the top/bottom peek thumbs into the viewport edge. */
const PeekFade = ({ side, height }: { side: "top" | "bottom"; height: number }) => (
  <div
    aria-hidden="true"
    className={cn("pointer-events-none absolute inset-x-0", side === "top" ? "top-0" : "bottom-0")}
    style={{
      height: `${height}px`,
      background: `linear-gradient(to ${side === "top" ? "bottom" : "top"}, ${SLOT_STYLE.peekFadeColorStart} 0%, ${SLOT_STYLE.peekFadeColorEnd} 100%)`,
    }}
  />
);

const PaginationDots = ({
  templates,
  activeIndex,
  goTo,
  className,
}: {
  templates: ReadonlyArray<TemplateDefinition>;
  activeIndex: number;
  goTo: (i: number) => void;
  className?: string;
}) => (
  <div aria-hidden="true" className={cn("flex shrink-0 flex-col items-center justify-center gap-1.5", className)}>
    {templates.map((t, i) => (
      <button
        aria-current={i === activeIndex ? "true" : undefined}
        aria-label={`${strings.templates.panel.featured.goToSlideAriaLabel} ${i + 1}`}
        className="-m-2 flex cursor-pointer items-center justify-center p-2"
        key={t.slug}
        type="button"
        onClick={(e) => {
          // The card itself loads the active example; a dot only moves the carousel.
          e.stopPropagation();
          goTo(i);
        }}
      >
        <span
          aria-hidden="true"
          className={cn(
            "block w-1 rounded-full transition-all",
            i === activeIndex
              ? "h-2.5 bg-foreground-primary"
              : "h-1 bg-foreground-disabled hover:bg-foreground-secondary",
          )}
        />
      </button>
    ))}
  </div>
);

const TryPromptCta = ({ onClick }: { onClick: () => void }) => (
  <button
    className="group/cta flex w-fit cursor-pointer items-center text-foreground-primary legal"
    type="button"
    onClick={(e) => {
      // Card-level click would select the same template again; keep it to one call.
      e.stopPropagation();
      onClick();
    }}
  >
    {strings.templates.card.cta}
    <span className="inline-block transition-transform duration-300 group-hover/cta:translate-x-1">
      <Icon aria-hidden="true" className="ml-1 size-4" name="arrow-right" />
    </span>
  </button>
);

/** Embla viewport + slides + edge fades. Tween is driven imperatively by the parent. */
const EmblaCarouselViewport = ({
  templates,
  config,
  selectedIndex,
  emblaRef,
  onSlideClick,
}: {
  templates: ReadonlyArray<TemplateDefinition>;
  config: CarouselConfig;
  selectedIndex: number;
  emblaRef: ReturnType<typeof useEmblaCarousel>[0];
  onSlideClick: (index: number) => void;
}) => {
  const peekHeight = (config.viewportHeight - config.featuredSize) / 2;

  return (
    <div
      aria-roledescription="carousel"
      className="relative shrink-0 overflow-hidden"
      ref={emblaRef}
      style={{ width: `${config.viewportWidth}px`, height: `${config.viewportHeight}px` }}
    >
      <div
        // Spacing in each slide's symmetric top+bottom padding (not flex
        // `gap`) so embla's loop clones carry leading + trailing space too,
        // and the active Slot ends up centered in the viewport. Don't add a
        // negative margin-top here: embla's `offsetTop`-based centering
        // ignores it and the peeks come out asymmetric.
        className="flex h-full w-full flex-col items-center [touch-action:pan-x_pinch-zoom]"
      >
        {templates.map((template, i) => (
          <button
            aria-label={template.description ? `${template.title}: ${template.description}` : template.title}
            aria-roledescription="slide"
            className="relative flex shrink-0 grow-0 cursor-pointer items-center justify-center"
            key={template.slug}
            style={{
              flexBasis: `${config.featuredSize + config.slideSpacing}px`,
              paddingTop: `${config.slideSpacing / 2}px`,
              paddingBottom: `${config.slideSpacing / 2}px`,
              width: `${config.featuredSize}px`,
              transform: "translate3d(0,0,0)",
            }}
            type="button"
            onClick={(e) => {
              // A peeking thumbnail scrolls itself into the centre; only the centred one selects.
              e.stopPropagation();
              onSlideClick(i);
            }}
          >
            <Slot config={config} isActive={i === selectedIndex} template={template} />
          </button>
        ))}
      </div>

      {peekHeight > 0 && <PeekFade height={peekHeight} side="top" />}
      {peekHeight > 0 && <PeekFade height={peekHeight} side="bottom" />}
    </div>
  );
};

/**
 * Full text panel (desktop + mobile-stacked). `key={slug}` retriggers the
 * `animate-in fade-in` on slide change.
 */
const FullTextPanel = ({
  template,
  className,
  onCtaClick,
}: {
  template: TemplateDefinition;
  className?: string;
  onCtaClick: () => void;
}) => (
  <div className={cn("flex flex-col duration-500 animate-in fade-in", className)} key={template.slug}>
    <div className="truncate text-foreground-secondary legal">{template.title}</div>
    <div className="min-w-0 space-y-1">
      <div className="line-clamp-2 text-foreground-primary body">{template.description}</div>
      {template.subdescription && (
        <div className="line-clamp-2 text-foreground-secondary label-2">{template.subdescription}</div>
      )}
    </div>
    <TryPromptCta onClick={onCtaClick} />
  </div>
);

/**
 * Outer "card" wrapper. Children supply the variant-specific arrangement.
 *
 * The whole card is a tap target for the centred example: a click anywhere that isn't one of the inner controls
 * (slide thumbnails, pagination dots, the CTA) loads it. It stays a `region` rather than becoming a `<button>`
 * because it contains those buttons; the CTA is still the keyboard path, so no keyboard affordance is lost.
 */
const CarouselRegion = ({
  className,
  children,
  onCardClick,
}: {
  className?: string;
  children: React.ReactNode;
  onCardClick: () => void;
}) => (
  <div
    aria-label={strings.templates.panel.featuredAriaLabel}
    className={cn("relative flex w-full cursor-pointer rounded-2xl bg-background-alt-2", className)}
    data-testid="featured-card"
    role="region"
    onClick={onCardClick}
  >
    {children}
  </div>
);

const FeaturedCarousel = ({ templates, onSelect, variant = "panel" }: FeaturedCarouselProps) => {
  // SSR can't detect viewport; flip to the real `useIsMobile` value on mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const detectedIsStacked = useIsMobile(STACKED_LAYOUT_BREAKPOINT_PX);
  const isStacked = mounted && detectedIsStacked;
  const isInlineCompact = isStacked && variant === "inline";
  const config = isInlineCompact ? COMPACT_CAROUSEL : isStacked ? MOBILE_CAROUSEL : CAROUSEL;

  const prefersReducedMotion = usePrefersReducedMotion();

  const plugins = useMemo(
    () =>
      prefersReducedMotion
        ? []
        : [
            Autoplay({
              delay: AUTOPLAY_DELAY_MS,
              stopOnInteraction: false,
              stopOnMouseEnter: true,
              stopOnFocusIn: true,
            }),
          ],
    [prefersReducedMotion],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: templates.length > 1,
      align: "center",
      axis: "y",
      containScroll: false,
      skipSnaps: false,
    },
    plugins,
  );

  const tweenFactor = useRef(0);
  const tweenNodes = useRef<{ thumb: HTMLElement; halo: HTMLElement }[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Hoisted out of `applyTween` so the regex doesn't run per scroll frame.
  const { minScale, maxBlurPx } = useMemo(
    () => ({
      minScale: config.adjacentSize / config.featuredSize,
      maxBlurPx: parseMaxBlurPx(config.inactiveFilter),
    }),
    [config],
  );

  const setTweenNodes = useCallback((api: EmblaCarouselType): void => {
    tweenNodes.current = api.slideNodes().map((slideNode) => ({
      thumb: slideNode.querySelector<HTMLElement>("[data-tween-thumb]")!,
      halo: slideNode.querySelector<HTMLElement>("[data-tween-halo]")!,
    }));
  }, []);

  const setTweenFactor = useCallback((api: EmblaCarouselType): void => {
    tweenFactor.current = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
  }, []);

  const applyTween = useCallback(
    (api: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = api.internalEngine();
      const scrollProgress = api.scrollProgress();
      const slidesInView = api.slidesInView();
      const isScrollEvent = eventName === "scroll";

      api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diff = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];
        if (!slidesInSnap) return;

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();
              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);
                if (sign === -1) diff = scrollSnap - (1 + scrollProgress);
                if (sign === 1) diff = scrollSnap + (1 - scrollProgress);
              }
            });
          }

          const t = clamp(1 - Math.abs(diff * tweenFactor.current), 0, 1);
          const scale = minScale + (1 - minScale) * t;
          const blur = (1 - t) * maxBlurPx;
          const saturate = 1 + 0.3 * (1 - t);
          const haloOpacity = t * SLOT_STYLE.haloOpacity;
          const shadow = t > 0.5 ? SLOT_STYLE.activeBoxShadow : "0 0 0 rgba(0,0,0,0)";

          const nodes = tweenNodes.current[slideIndex];
          if (!nodes) return;
          nodes.thumb.style.transform = `scale(${scale})`;
          nodes.thumb.style.filter = `blur(${blur}px) saturate(${saturate})`;
          nodes.thumb.style.boxShadow = shadow;
          nodes.halo.style.opacity = haloOpacity.toString();
        });
      });
    },
    [minScale, maxBlurPx],
  );

  const onSlideClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      if (index === emblaApi.selectedScrollSnap()) {
        onSelect(templates[index]!);
      } else {
        emblaApi.scrollTo(index);
      }
    },
    [emblaApi, onSelect, templates],
  );

  const onSelectActive = useCallback(() => {
    if (!emblaApi) return;
    const t = templates[emblaApi.selectedScrollSnap()];
    if (t) onSelect(t);
  }, [emblaApi, onSelect, templates]);

  // A swipe ends in a click, so the card-level tap target has to ignore it. Embla brackets a drag with
  // pointerDown/pointerUp; any real scroll movement between them means the gesture was a swipe.
  const dragStartProgress = useRef<number | null>(null);
  const wasDragged = useRef(false);

  const handleCardClick = useCallback(() => {
    if (wasDragged.current) {
      wasDragged.current = false;
      return;
    }
    onSelectActive();
  }, [onSelectActive]);

  const goTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelectChange = (api: EmblaCarouselType) => setSelectedIndex(api.selectedScrollSnap());

    const onPointerDown = (api: EmblaCarouselType) => {
      wasDragged.current = false;
      dragStartProgress.current = api.scrollProgress();
    };

    const onPointerUp = (api: EmblaCarouselType) => {
      const start = dragStartProgress.current;
      dragStartProgress.current = null;
      if (start !== null && Math.abs(api.scrollProgress() - start) > DRAG_PROGRESS_EPSILON) {
        wasDragged.current = true;
      }
    };

    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    applyTween(emblaApi);
    onSelectChange(emblaApi);

    emblaApi
      .on("reInit", setTweenNodes)
      .on("reInit", setTweenFactor)
      .on("reInit", applyTween)
      .on("scroll", applyTween)
      .on("slideFocus", applyTween)
      .on("reInit", onSelectChange)
      .on("select", onSelectChange)
      .on("pointerDown", onPointerDown)
      .on("pointerUp", onPointerUp);

    return () => {
      // Detach so handlers don't accumulate when `applyTween` changes.
      emblaApi
        .off("reInit", setTweenNodes)
        .off("reInit", setTweenFactor)
        .off("reInit", applyTween)
        .off("scroll", applyTween)
        .off("slideFocus", applyTween)
        .off("reInit", onSelectChange)
        .off("select", onSelectChange)
        .off("pointerDown", onPointerDown)
        .off("pointerUp", onPointerUp);
    };
  }, [emblaApi, setTweenNodes, setTweenFactor, applyTween]);

  if (templates.length === 0) return null;
  // Skip SSR so the desktop CAROUSEL variant doesn't briefly render on
  // mobile before hydration swaps to MOBILE / COMPACT sizing.
  if (!mounted) return null;
  const activeTemplate = templates[selectedIndex] ?? templates[0]!;

  const viewport = (
    <EmblaCarouselViewport
      config={config}
      emblaRef={emblaRef}
      selectedIndex={selectedIndex}
      templates={templates}
      onSlideClick={onSlideClick}
    />
  );

  if (isInlineCompact) {
    return (
      <CarouselRegion className="items-stretch overflow-hidden" onCardClick={handleCardClick}>
        {/* pl-3 → ~18px gap between dots and thumb. */}
        <div className="relative flex shrink-0 items-center pl-3">
          {viewport}
          {templates.length > 1 && (
            <PaginationDots
              activeIndex={selectedIndex}
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2"
              goTo={goTo}
              templates={templates}
            />
          )}
        </div>
        <div
          className="flex min-w-0 flex-1 flex-col justify-center gap-1 p-4 duration-500 animate-in fade-in"
          key={activeTemplate.slug}
        >
          <div className="truncate text-foreground-secondary legal">{activeTemplate.title}</div>
          <div className="line-clamp-1 text-foreground-primary body">{activeTemplate.description}</div>
          <TryPromptCta onClick={onSelectActive} />
        </div>
      </CarouselRegion>
    );
  }

  if (isStacked) {
    return (
      <CarouselRegion className="flex-col overflow-hidden" onCardClick={handleCardClick}>
        <div className="relative flex w-full items-center justify-center">
          {viewport}
          {templates.length > 1 && (
            <PaginationDots
              activeIndex={selectedIndex}
              className="absolute bottom-0 left-4 top-0 z-10"
              goTo={goTo}
              templates={templates}
            />
          )}
        </div>
        <FullTextPanel className="gap-4 px-6 py-4" template={activeTemplate} onCtaClick={onSelectActive} />
      </CarouselRegion>
    );
  }

  return (
    <CarouselRegion className="items-center overflow-hidden" onCardClick={handleCardClick}>
      {templates.length > 1 && (
        // Asymmetric pad: `pl-4` keeps dots off the card edge; `pr-2` is small
        // because the viewport already carries its own cross-axis buffer.
        <PaginationDots activeIndex={selectedIndex} className="z-10 pl-4 pr-2" goTo={goTo} templates={templates} />
      )}
      {viewport}
      <FullTextPanel
        className="min-w-0 flex-1 justify-between self-stretch px-6 py-6"
        template={activeTemplate}
        onCtaClick={onSelectActive}
      />
    </CarouselRegion>
  );
};

export default FeaturedCarousel;
